import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { PartyPopper, BadgeIndianRupee } from "lucide-react";

const COLORS = ["#22c55e", "#4ade80", "#fbbf24", "#f59e0b", "#ffffff", "#2563EB", "#DB2777"];

export default function MonetizationCelebration({ onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    const W = canvas.width, H = canvas.height;
    const parts = [];
    for (let b = 0; b < 4; b++) {
      const cx = W * (0.2 + 0.2 * b), cy = H * (0.3 + (b % 2) * 0.15);
      for (let i = 0; i < 60; i++) {
        const a = Math.random() * Math.PI * 2, sp = (2 + Math.random() * 6) * dpr;
        parts.push({ x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2 * dpr,
                     life: 1, decay: 0.004 + Math.random() * 0.008,
                     size: (2 + Math.random() * 4) * dpr, color: COLORS[(Math.random() * COLORS.length) | 0],
                     rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.2 });
      }
    }
    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const p of parts) {
        if (p.life <= 0) continue;
        alive = true;
        p.x += p.vx; p.y += p.vy; p.vy += 0.08 * dpr; p.vx *= 0.99; p.life -= p.decay; p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
      if (alive) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="monetization-celebration">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" />
      <motion.div initial={{ scale: 0.7, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="relative bg-card border border-border rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-2xl">
        <motion.div initial={{ rotate: -20, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ delay: 0.15, type: "spring" }}
          className="mx-auto h-20 w-20 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30"
          style={{ background: "linear-gradient(135deg,#007A4D,#22c55e)" }}>
          <PartyPopper className="h-10 w-10 text-white" />
        </motion.div>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-6 tracking-tight" data-testid="celebration-title">Congratulations!</h2>
        <p className="mt-2 text-[15px]">You're officially a <span className="text-emerald-500 font-extrabold">Monetized Creator</span> on MPlay.</p>
        <p className="text-sm text-muted-foreground mt-2">View monetization is now unlocked — turn it on for your published videos and earn from every 1,000 eligible views.</p>
        <button onClick={onClose} data-testid="celebration-close"
          className="mt-7 w-full h-12 rounded-full font-extrabold text-white flex items-center justify-center gap-2 transition-transform active:scale-[.98]"
          style={{ background: "linear-gradient(135deg,#007A4D,#22c55e)" }}>
          <BadgeIndianRupee className="h-5 w-5" /> Start Earning
        </button>
      </motion.div>
    </div>
  );
}
